import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateInvitation } from '@/api/auth/invitations/invitations'
import { getGetOrganizationRolesQueryKey } from '@/api/auth/roles/roles'
import type { CreateInvitationRequestRole } from '@/api/auth/missionImpactAuthAPI.schemas'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

interface InviteUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
}

export function InviteUserDialog({ open, onOpenChange, orgId }: InviteUserDialogProps) {
  const queryClient = useQueryClient()
  const { handleApiError } = useError();
  const { t } = useLanguage();
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<CreateInvitationRequestRole>('Reader')
  const [roleExpirationDate, setRoleExpirationDate] = useState<Date | undefined>(undefined)

  const { mutate: inviteUser, isPending } = useCreateInvitation({
    mutation: {
      onSuccess: () => {
        toast.success(t.dialogs.inviteUser.userInvitedSuccessfully)
        void queryClient.invalidateQueries({
          queryKey: getGetOrganizationRolesQueryKey(orgId)
        })
        // Reset form
        setEmail('')
        setRole('Reader')
        setRoleExpirationDate(undefined)
        onOpenChange(false)
      },
      onError: (error) => {
        handleApiError(error, { context: 'InviteUserDialog.inviteUser' })
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Calculate invitation expiration: 7 days from now in UTC
    const invitationExpiration = new Date()
    invitationExpiration.setUTCDate(invitationExpiration.getUTCDate() + 7)

    inviteUser({
      orgId,
      data: {
        email: email.trim(),
        role,
        invitationExpiresUtc: invitationExpiration.toISOString(),
        membershipExpiresUtc: roleExpirationDate?.toISOString()
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t.dialogs.inviteUser.title}</DialogTitle>
          <DialogDescription>{t.dialogs.inviteUser.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t.dialogs.inviteUser.email}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t.dialogs.inviteUser.emailPlaceholder}
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">{t.dialogs.inviteUser.role}</Label>
              <Select value={role} onValueChange={(value) => { setRole(value as CreateInvitationRequestRole); }}>
                <SelectTrigger id="role">
                  <SelectValue placeholder={t.dialogs.inviteUser.rolePlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Reader">{t.dialogs.inviteUser.roleReader}</SelectItem>
                  <SelectItem value="Operator">{t.dialogs.inviteUser.roleOperator}</SelectItem>
                  <SelectItem value="Administrator">{t.dialogs.inviteUser.roleAdministrator}</SelectItem>
                  <SelectItem value="Billing">{t.dialogs.inviteUser.roleBillingManager}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="roleExpiration">{t.dialogs.inviteUser.roleExpiration}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="roleExpiration"
                    variant="outline"
                    className={cn(
                      'justify-start text-left font-normal',
                      !roleExpirationDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {roleExpirationDate ? format(roleExpirationDate, 'PPP') : t.dialogs.inviteUser.roleExpirationPlaceholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="min-h-[320px]">
                    <Calendar
                      mode="single"
                      selected={roleExpirationDate}
                      onSelect={setRoleExpirationDate}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      autoFocus
                    />
                  </div>
                </PopoverContent>
              </Popover>
              {roleExpirationDate && (
                <p className="text-xs text-muted-foreground">
                  {t.dialogs.inviteUser.roleExpiresOn.replace('{date}', format(roleExpirationDate, 'PPP'))}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {t.dialogs.inviteUser.invitationExpiresNote}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { onOpenChange(false); }} disabled={isPending}>
              {t.dialogs.inviteUser.cancel}
            </Button>
            <LoadingButton type="submit" isLoading={isPending}>
              {t.dialogs.inviteUser.sendInvite}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
